import { useMemo } from 'react';
import { useQuery, type UseQueryOptions, keepPreviousData } from '@tanstack/react-query';

import { getPartnerProductPrices } from '@apis/partner';
import type { PageResponse } from '@models/api';
import type { PartnerProductPriceResponse } from '@models/partner';
import type { GetProductsParams } from '@apis/product';

import { dropEmptyValues } from '@utils/common';

const SEARCH_KEYS: Array<keyof GetProductsParams> = [
  'productCode',
  'productName',
  'stockId',
  'countryId',
  'productStatus',
  'productSaleType',
];

export const usePartnerProductPrices = ({
  page,
  pageSize,
  partnerId,
  searchParam,
}: {
  page: number;
  pageSize: number;
  partnerId: number;
  searchParam: GetProductsParams;
}) => {
  /**
   * Normalize query inputs so react-query cache keys stay stable
   * (avoid object identity cache misses + unnecessary refetches).
   */
  const cleaned = useMemo(
    () => dropEmptyValues(searchParam, SEARCH_KEYS),
    [searchParam],
  );

  const queryOptions: UseQueryOptions<PageResponse<PartnerProductPriceResponse>, Error> = {
    queryKey: ['partnerProductPrices', partnerId, page, pageSize, cleaned],
    queryFn: () =>
      getPartnerProductPrices(partnerId, {
        pageNo: page,
        pageSize,
        ...cleaned,
      }),
    enabled: Boolean(partnerId),

    // Keep previous page results during pagination for smoother UX.
    placeholderData: keepPreviousData,
  };

  return useQuery(queryOptions);
};
